import React, { useEffect, useState } from "react";
import { Bot, X, MessageCircle } from "lucide-react";
import { AIManagerChat } from "@/components/AIManagerChat";
import { useAIManagerName } from "@/hooks/useAIManagerName";

/**
 * Floating AI Manager chat widget shown on every agent page. Bottom-right
 * bubble; expands into a compact chat panel. Uses the same session infra as
 * the admin AI Manager Chat tab.
 *
 * Persists open/closed state per-tab so the agent doesn't lose context when
 * navigating between routes.
 */
const STORAGE_KEY = "crm_ai_widget_open_v1";

export function AIManagerWidget() {
  const { name } = useAIManagerName();
  const [open, setOpen] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, open ? "1" : "0"); } catch {}
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {/* Launcher bubble — hidden while panel is open on mobile to avoid overlap */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ai-widget-open-btn"
          aria-label={`Open ${name}`}
          className="fixed bottom-24 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg ring-4 ring-primary/20 transition hover:scale-105 sm:bottom-6 sm:right-6"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="ai-widget-panel"
          className="fixed bottom-20 right-2 z-50 flex w-[calc(100vw-1rem)] max-w-md flex-col rounded-2xl border border-border bg-background shadow-2xl animate-fade-up sm:bottom-6 sm:right-6 sm:w-[420px]"
          style={{ height: "min(70vh, 620px)" }}
        >
          <header className="flex items-center gap-2 border-b border-border p-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary">
              <Bot className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm font-bold leading-tight truncate">{name}</div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Northline AI</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="ai-widget-close-btn"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </header>
          <div className="min-h-0 flex-1 overflow-hidden">
            <AIManagerChat compact role="agent" managerName={name} />
          </div>
        </div>
      )}
    </>
  );
}
